import React from "react";
import { format, isAfter } from "date-fns";
import { parseISOWithoutTime } from "helpers/date-helpers";
import PropTypes from "prop-types";
import { Faq, HELP, User } from "routes";
import { dateFnsFormats, membershipItemStates } from "rtr-constants";
import { isPauseWithItemsTier } from "helpers/membership-tier-helpers";
import { membershipStatePropType } from "components/propTypes";
import MembershipHelpers from "helpers/membership-helpers";
import { isPausedWithoutItems, isPausedWithItems, isFuturePausedWithoutItems } from "helpers/membership-plan-helpers";
import AtomErrorNotification from "components/source/atoms/atom-error-notification";
import MembershipAddSwapCTA from "components/source/membership/membership-add-swap-cta";
import { addCacheBusterToUrl } from "../../../helpers/location-helpers";

const NO_SLOTS_CLASSNAME = "no-slots";

export class MembershipFormFieldsBillingStatus extends React.Component {
  static propTypes = {
    hasFutureCancelDate: PropTypes.bool.isRequired,
    isFuturePausedWithoutItemsWithinThreeDays: PropTypes.bool.isRequired,
    isHomeGood: PropTypes.bool,
    isInPaymentRetry: PropTypes.bool.isRequired,
    membershipState: membershipStatePropType.isRequired,
    onOpenRetryModal: PropTypes.func.isRequired,
    onOpenAddShipmentModal: PropTypes.func.isRequired,
    isItemAvailable: PropTypes.bool.isRequired,
    productIsEligibleForUser: PropTypes.bool,
  };

  renderProductDrawerError = (additionalClassName, text, ctaText, errorURL, onClickCta) => {
    return (
      <AtomErrorNotification
        additionalClassName={`membership-form-fields__${additionalClassName}`}
        errorCopy={text}
        errorCTA={ctaText}
        errorURL={errorURL}
        onClick={onClickCta}
      />
    );
  };

  renderAddSwapCta() {
    const { membershipState = {} } = this.props;
    return <MembershipAddSwapCTA membershipState={membershipState} />;
  }

  renderNoOpenPickSlotsErrorMessaging() {
    const { membershipState = {} } = this.props;
    const nextPickDate = MembershipHelpers.fetchNextPickDate(membershipState);
    const formattedNextPickDate = format(parseISOWithoutTime(nextPickDate), dateFnsFormats.day);
    const text = `You can swap again anytime after ${formattedNextPickDate}.`;

    return this.renderProductDrawerError(NO_SLOTS_CLASSNAME, text);
  }

  renderNoOpenPickSlotsHomegoodErrorMessaging() {
    // home good products cannot be rented with Reserve
    const text = "Free up a spot to select this item.";

    return this.renderProductDrawerError(NO_SLOTS_CLASSNAME, text);
  }

  renderOverdueItemMemberState() {
    const additionalClassName = "retry-member";
    const text = "You have overdue items still at home. You won’t be able to swap again until they’re returned.";

    return this.renderProductDrawerError(additionalClassName, text);
  }

  renderWillBeAvailableSoonState() {
    const additionalClassName = "available-soon";
    const text = "This item will be available soon! Heart it now so you can rent it later.";
    const errorCTA = "Learn More";
    const errorURL = Faq.notAvailable;
    return this.renderProductDrawerError(additionalClassName, text, errorCTA, errorURL);
  }

  renderRetryMemberState() {
    const additionalClassName = "retry-member";
    const text = "Your monthly payment didn't go through. Make your payment now to keep your account active.";
    const onClickCta = this.props.onOpenRetryModal;
    const ctaText = "Update Now";

    return this.renderProductDrawerError(additionalClassName, text, ctaText, null, onClickCta);
  }

  renderIneligiblePlanMessaging(membershipState) {
    let text, onClickCta;
    const ctaText = "Add a Shipment.";

    if (MembershipHelpers.isEligibleForImmediateSwapUpgrade(membershipState)) {
      text = "This style is not included in your plan. To upgrade and access the full closet, ";
      onClickCta = this.props.onOpenAddShipmentModal;

      return this.renderProductDrawerError(NO_SLOTS_CLASSNAME, text, ctaText, null, onClickCta);
    }

    text = "This style is not included in your plan. To upgrade and access premium inventory, ";
    const errorURL = User.Settings;

    return this.renderProductDrawerError(NO_SLOTS_CLASSNAME, text, ctaText, errorURL, null);
  }

  renderFutureCancelMemberState() {
    const additionalClassName = "canceled-member";
    const text = "Your membership has been cancelled. Ready to rent again?";
    const ctaText = "Get in Touch";
    const errorURL = `${addCacheBusterToUrl(HELP.CONTACT)}`;

    return this.renderProductDrawerError(additionalClassName, text, ctaText, errorURL);
  }

  renderFuturePausedMemberState() {
    const { futureMembershipTierRevision, membershipPausedOn, membershipResumedOn, tierChangeStartedOn } =
      this.props.membershipState;
    let additionalClassName, text, errorCTA, errorURL;

    /** Checks 2 Future Paused states:
     * 1) Future Paused with Items
     * 2) Future Paused without Items — includes checks to ensure membershipPausedOn is actually AFTER the current date and
     * if member has no remaining shipments or is pausing within 3 days
     * */
    if (isPauseWithItemsTier(futureMembershipTierRevision) && tierChangeStartedOn) {
      additionalClassName = "future-pause-with-items-member";
      const formattedPauseBeginDate = format(parseISOWithoutTime(tierChangeStartedOn), dateFnsFormats.day);
      text = `Your membership will be paused starting ${formattedPauseBeginDate}.`;
      errorCTA = "Manage Membership";
      errorURL = User.Settings;
    } else if (
      membershipPausedOn &&
      membershipResumedOn &&
      isAfter(parseISOWithoutTime(membershipPausedOn), new Date()) &&
      (this.props.isFuturePausedWithoutItemsWithinThreeDays ||
        !MembershipHelpers.hasShipmentsCount(this.props.membershipState))
    ) {
      const formattedPauseBeginDate = format(parseISOWithoutTime(membershipPausedOn), dateFnsFormats.day);
      const formattedResumeDate = format(parseISOWithoutTime(membershipResumedOn), dateFnsFormats.day);
      additionalClassName = "future-paused-member";
      text = `Your membership will be paused from ${formattedPauseBeginDate} to ${formattedResumeDate}.`;
    } else {
      return null;
    }

    return this.renderProductDrawerError(additionalClassName, text, errorCTA, errorURL);
  }

  renderPausedMemberState() {
    /** Checks 2 Actively Paused states:
     * 1) Currently Paused with Items
     * 1) Currently Paused without Items
     * */
    const { nextBillingDate, previousTierResumesOn } = this.props.membershipState;

    let additionalClassName, formattedResumeDate, text, errorCTA, errorURL;

    if (previousTierResumesOn) {
      additionalClassName = "paused-with-items-member";
      formattedResumeDate = format(parseISOWithoutTime(previousTierResumesOn), dateFnsFormats.day);
      text = `Your membership is paused until ${formattedResumeDate}.`;
      errorCTA = "Manage Membership";
      errorURL = User.Settings;
    } else {
      additionalClassName = "paused-member";
      formattedResumeDate = format(parseISOWithoutTime(nextBillingDate), dateFnsFormats.day);
      text = `Your membership is paused until ${formattedResumeDate}.`;
      errorCTA = "Manage Membership";
      errorURL = User.Settings;
    }

    return this.renderProductDrawerError(additionalClassName, text, errorCTA, errorURL);
  }

  render() {
    const {
      hasFutureCancelDate,
      isHomeGood,
      isInPaymentRetry,
      membershipState,
      isFuturePausedWithoutItemsWithinThreeDays,
      isItemAvailable,
      productIsEligibleForUser,
    } = this.props;

    if (
      MembershipHelpers.countOfItemTypes(membershipState, membershipItemStates.OVERDUE) > 0 &&
      !MembershipHelpers.isEligibleToPick(membershipState)
    ) {
      return this.renderOverdueItemMemberState();
    }

    if (membershipState.billingStatus === "PAUSED") {
      return this.renderPausedMemberState();
    }

    if (
      (isPauseWithItemsTier(membershipState.futureMembershipTierRevision) ||
        isPausedWithoutItems(membershipState) ||
        isPausedWithItems(membershipState) ||
        isFuturePausedWithoutItems(membershipState.membershipPausedOn)) &&
      Number.isInteger(membershipState.monthlyShipmentLimit)
    ) {
      if (
        isFuturePausedWithoutItemsWithinThreeDays ||
        !MembershipHelpers.isEligibleForImmediateSwapUpgrade(membershipState)
      ) {
        return this.renderFuturePausedMemberState();
      }
    }

    if (hasFutureCancelDate) {
      return this.renderFutureCancelMemberState();
    }

    if (isInPaymentRetry) {
      return this.renderRetryMemberState();
    }

    if (!productIsEligibleForUser) {
      return this.renderIneligiblePlanMessaging(membershipState);
    }

    // If there are no pick slots open or a future legacy pause user is more than 3 days from their pause
    if (
      !MembershipHelpers.fetchNumOpenSlots(membershipState) &&
      (!isFuturePausedWithoutItems(membershipState.membershipPausedOn) || !isFuturePausedWithoutItemsWithinThreeDays)
    ) {
      if (MembershipHelpers.isEligibleForImmediateSwapUpgrade(membershipState)) {
        return this.renderAddSwapCta();
      } else if (
        MembershipHelpers.isFixedSwaps(membershipState) &&
        !MembershipHelpers.hasShipmentsCount(membershipState)
      ) {
        return this.renderNoOpenPickSlotsErrorMessaging();
      } else if (isHomeGood) {
        // home good products cannot be rented with Reserve
        return this.renderNoOpenPickSlotsHomegoodErrorMessaging();
      }
    }

    if (!isItemAvailable) {
      return this.renderWillBeAvailableSoonState();
    }

    return null;
  }
}

export default MembershipFormFieldsBillingStatus;
